import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Quiz from "@/components/Quiz";
import { t } from "@/i18n";

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [placement, setPlacement] = useState(null);
  const [goal, setGoal] = useState(10);
  const [level, setLevel] = useState("A1");
  const [readingAssessment, setReadingAssessment] = useState(null);
  const { refresh } = useAuth();
  const nav = useNavigate();

  const startTest = async () => {
    const { data } = await api.get("/placement");
    setPlacement(data.questions.map((q) => ({ ...q, answer: -1, skill: "grammar" })));
    setStep(1);
    setPlacement((p) => p); // noop
    // fetch answers server-side; we grade by submitting picks
  };

  // For onboarding we just collect answer indices and submit.
  const [answers, setAnswers] = useState([]);
  const finishTest = async (finalAnswers = answers) => {
    const { data } = await api.post("/placement", finalAnswers);
    setLevel(data.level);
    setReadingAssessment(data.reading_assessment || null);
    setStep(data.reading_assessment ? 3 : 2);
  };

  const finish = async () => {
    await api.post("/onboarding", {
      level,
      daily_goal: goal,
      reading_assessment: readingAssessment || {
        reading_level: "beginner",
        alphabet_priority: true,
        question_a: 3,
        question_b: 2,
      },
    });
    await refresh();
    nav("/dashboard");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-lg">
        {step === 0 && (
          <div className="text-center" data-testid="onboarding-welcome">
            <div className="arabic text-6xl mb-4 text-primary">أهلاً بك</div>
            <h1 className="font-head text-3xl font-bold mb-3">{t("onboarding.bienvenue_sur_harf")}</h1>
            <p className="text-muted-foreground mb-6">{t("onboarding.le_cecrl_classe_les_langues_de")}</p>
            <div className="flex flex-col gap-3">
              <button data-testid="start-placement" onClick={startTest}
                className="py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition">
                 {t("onboarding.faire_le_test_de_positionnement_12")}
              </button>
              <button data-testid="skip-placement" onClick={() => setStep(2)}
                className="py-3 rounded-xl border font-medium hover:bg-secondary transition">
                {t("onboarding.je_debute_de_zero_commencer_au")}
              </button>
            </div>
          </div>
        )}
        {step === 1 && placement && (
          <PlacementQuiz
            questions={placement}
            onDone={(finalAnswers) => {
              setAnswers(finalAnswers);
              finishTest(finalAnswers);
            }}
            setAnswers={setAnswers}
          />
        )}
        {step === 2 && !readingAssessment && (
          <ReadingAssessment onDone={(assessment) => {
            setReadingAssessment(assessment);
            setStep(3);
          }} />
        )}
        {step === 3 || (step === 2 && readingAssessment) ? (
          <div data-testid="onboarding-goal">
            <h1 className="font-head text-3xl font-bold mb-2">{t("onboarding.ton_objectif_quotidien")}</h1>
            <p className="text-muted-foreground mb-2">{t("onboarding.niveau_de_depart_estime")} <span className="font-semibold text-primary">{level}</span></p>
            <p className="text-muted-foreground mb-6 text-sm">{t("onboarding.il_calibre_ton_plan_du_jour")}</p>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[5, 10, 20].map((g) => (
                <button key={g} data-testid={`goal-${g}`} onClick={() => setGoal(g)}
                  className={`py-6 rounded-xl border-2 transition ${goal === g ? "border-primary bg-primary/10" : "hover:border-primary/50"}`}>
                  <div className="font-head text-2xl font-bold">{g}</div>
                  <div className="text-xs text-muted-foreground">{t("onboarding.min_jour")}</div>
                </button>
              ))}
            </div>
            <button data-testid="finish-onboarding" onClick={finish}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition">
              {t("onboarding.commencer_a_apprendre")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ReadingAssessment({ onDone }) {
  const [question, setQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const questions = [
    {
      prompt: "Sais-tu lire les lettres arabes lorsqu'elles sont attachées entre elles pour former un mot ?",
      options: [
        "Oui, couramment",
        "Oui, mais je déchiffre lentement",
        "Non, je connais seulement quelques lettres isolées",
        "Non, je pars de zéro complet",
      ],
    },
    {
      prompt: "Connais-tu le système des voyelles courtes (les Harakat : Fatha, Kasra, Damma) ?",
      options: ["Oui je les maîtrise", "J'en ai une vague idée", "Pas du tout"],
    },
  ];
  const current = questions[question];
  const pick = (idx) => {
    const next = [...answers, idx];
    if (question === questions.length - 1) {
      const [a, b] = next;
      const alphabetPriority = a >= 1 || b >= 1;
      onDone({
        question_a: a,
        question_b: b,
        reading_level: a === 0 && b === 0 ? "fluent" : (a === 1 || b === 1 ? "developing" : "beginner"),
        alphabet_priority: alphabetPriority,
      });
    } else {
      setAnswers(next);
      setQuestion(question + 1);
    }
  };
  return (
    <div className="rounded-2xl border bg-card p-6" data-testid="reading-assessment">
      <div className="text-xs text-muted-foreground mb-3">{t("onboarding.evaluation_de_lecture")} {question + 1}/2</div>
      <h1 className="font-head text-2xl font-bold mb-2">{t("onboarding.pour_mieux_te_guider")}</h1>
      <p className="font-medium mb-5">{current.prompt}</p>
      <div className="space-y-2.5">
        {current.options.map((option, idx) => (
          <button
            key={option}
            type="button"
            onClick={() => pick(idx)}
            className="w-full text-left px-4 py-3 rounded-xl border hover:border-primary transition"
          >
            {idx + 1}. {option}
          </button>
        ))}
      </div>
    </div>
  );
}

function PlacementQuiz({ questions, onDone, setAnswers }) {
  const [i, setI] = useState(0);
  const [picks, setPicks] = useState([]);
  const q = questions[i];
  const pick = (idx) => {
    const np = [...picks]; np[i] = idx; setPicks(np); setAnswers(np);
    if (i + 1 >= questions.length) { setTimeout(() => onDone(np), 100); }
    else setI(i + 1);
  };
  return (
    <div className="rounded-2xl border bg-card p-6" data-testid="placement-quiz">
      <div className="text-xs text-muted-foreground mb-3">{t("onboarding.test")} {i + 1}/{questions.length}</div>
      <p className="font-medium mb-5">{q.prompt}</p>
      <div className="space-y-2.5">
        {q.options.map((o, idx) => (
          <button key={idx} data-testid={`placement-opt-${idx}`} onClick={() => pick(idx)}
            className="w-full text-left px-4 py-3 rounded-xl border hover:border-primary transition">{o}</button>
        ))}
      </div>
    </div>
  );
}
